import { css } from "@emotion/css";
import { RadioButtonGroup, RadioButton, TextField, MultiSelect, NumberField } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentType, EnvironmentV2Resource, FeatureToggleEnvironmentResource, Segment, TenantResource, TenantTargetingStrategy, ToggleStrategy } from "@octopusdeploy/octopus-server-client";
import { Permission, Repository, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import pluralize from "pluralize";
import { ExpandableFormSection, Summary } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import type { DrawerWorkflow } from "~/workflows/workflows";
import { workflows } from "~/workflows/workflows";
import { ConfigurableEnvironmentSelect } from "../ConfigurableEnvironmentSelect";
import EnvironmentOrMissingChip from "../EnvironmentOrMissingChip";
import { SegmentList } from "../FeatureToggleEnvironment";
import { SegmentEditList } from "../SegmentEditList";
export interface AddFeatureToggleEnvironmentWorkflowInput {
    spaceId: string;
    projectId: string;
    tenantedDeploymentMode: TenantedDeploymentMode;
}
export interface AddFeatureToggleEnvironmentWorkflowProps {
    workflowInput: AddFeatureToggleEnvironmentWorkflowInput;
    loaderData: FeatureToggleEnvironmentWorkflowLoaderData;
    completeWorkflow: (output: FeatureToggleEnvironmentResource) => void;
}
export function AddFeatureToggleEnvironmentWorkflow({ workflowInput, loaderData, completeWorkflow }: AddFeatureToggleEnvironmentWorkflowProps) {
    return <FeatureToggleEnvironmentWorkflow featureToggleEnvironment={undefined} loaderData={loaderData} completeWorkflow={completeWorkflow} workflow={workflows.addFeatureToggleEnvironmentWorkflow} workflowInput={workflowInput}/>;
}
export interface EditFeatureToggleEnvironmentWorkflowInput {
    spaceId: string;
    projectId: string;
    featureToggleEnvironment: FeatureToggleEnvironmentResource;
    tenantedDeploymentMode: TenantedDeploymentMode;
}
export interface EditFeatureToggleEnvironmentWorkflowProps {
    workflowInput: EditFeatureToggleEnvironmentWorkflowInput;
    loaderData: FeatureToggleEnvironmentWorkflowLoaderData;
    completeWorkflow: (output: FeatureToggleEnvironmentResource) => void;
}
export function EditFeatureToggleEnvironmentWorkflow({ workflowInput, loaderData, completeWorkflow }: EditFeatureToggleEnvironmentWorkflowProps) {
    return (<FeatureToggleEnvironmentWorkflow featureToggleEnvironment={workflowInput.featureToggleEnvironment} loaderData={loaderData} completeWorkflow={completeWorkflow} workflow={workflows.editFeatureToggleEnvironmentWorkflow} workflowInput={workflowInput}/>);
}
export interface FeatureToggleEnvironmentWorkflowLoaderData {
    configurableEnvironments: EnvironmentV2Resource[];
    allEnvironments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tenantTags: string[];
}
// Shared implementation of the Add/Edit Feature Toggle Environment Workflow
export async function featureToggleEnvironmentWorkflowLoader(repository: Repository, spaceId: string, projectId: string): Promise<FeatureToggleEnvironmentWorkflowLoaderData> {
    const environmentTypes: EnvironmentType[] = ["Static", "Ephemeral"];
    const configurableEnvironments = repository.Projects.getProjectEnvironments(projectId, 0, Repository.takeAll, environmentTypes);
    const allEnvironments = repository.Environments.getV2({ spaceId: spaceId, skip: 0, take: Repository.takeAll, type: environmentTypes });
    const tenants = repository.Tenants.all({ projectId: projectId });
    // This gets all tenant tags in the current space. Tag set view permission is currently unrestricted.
    const tenantTagSets = repository.TagSets.getAllByScopes(["Tenant"]);
    return {
        configurableEnvironments: (await configurableEnvironments).Items,
        allEnvironments: (await allEnvironments).Items,
        tenants: await tenants,
        tenantTags: (await tenantTagSets).sort((a, b) => a.SortOrder - b.SortOrder).flatMap((set) => set.Tags.sort((a, b) => a.SortOrder - b.SortOrder).map((tag) => tag.CanonicalTagName)),
    };
}
interface FeatureToggleEnvironmentWorkflowProps {
    featureToggleEnvironment: FeatureToggleEnvironmentResource | undefined;
    loaderData: FeatureToggleEnvironmentWorkflowLoaderData;
    completeWorkflow: (output: FeatureToggleEnvironmentResource) => void;
    workflow: DrawerWorkflow<AddFeatureToggleEnvironmentWorkflowInput | EditFeatureToggleEnvironmentWorkflowInput, void>;
    workflowInput: AddFeatureToggleEnvironmentWorkflowInput | EditFeatureToggleEnvironmentWorkflowInput;
}
interface EditFeatureToggleEnvironmentState {
    environmentId: string | undefined;
    tenantTargetingStrategy: TenantTargetingStrategy;
    tenantIds: string[];
    rolloutPercentage?: number;
    excludedTenantIds: string[];
    tenantTags: string[];
    excludedTenantTags: string[];
    segments: Segment[];
    minimumVersion?: string;
}
const styles = {
    // Apply gap between form elements until this is built into the components.
    formElementContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
};
function FeatureToggleEnvironmentWorkflow({ featureToggleEnvironment, loaderData, completeWorkflow, workflow, workflowInput }: FeatureToggleEnvironmentWorkflowProps) {
    const adding = featureToggleEnvironment === undefined;
    const title = adding ? "Add Environment" : `Edit Environment`;
    const showTenantTagTargetedToggles = useOctopusFeatureToggle("tenant-tag-targeted-feature-toggles", false);
    const { configurableEnvironments, allEnvironments, tenants, tenantTags } = loaderData;
    const initialModel: EditFeatureToggleEnvironmentState = {
        environmentId: featureToggleEnvironment ? featureToggleEnvironment.DeploymentEnvironmentId : undefined,
        tenantTargetingStrategy: featureToggleEnvironment ? featureToggleEnvironment.TenantTargetingStrategy : "AllTenants",
        tenantIds: featureToggleEnvironment ? featureToggleEnvironment.TenantIds : [],
        rolloutPercentage: featureToggleEnvironment?.RolloutPercentage ?? undefined,
        excludedTenantIds: featureToggleEnvironment ? featureToggleEnvironment.ExcludedTenantIds : [],
        tenantTags: featureToggleEnvironment?.TenantTags ?? [],
        excludedTenantTags: featureToggleEnvironment?.ExcludedTenantTags ?? [],
        segments: featureToggleEnvironment ? featureToggleEnvironment.Segments : [],
        minimumVersion: featureToggleEnvironment?.MinimumVersion ?? undefined,
    };
    const onChangeTenantTargetingStrategy = (strategy: TenantTargetingStrategy) => {
        if (strategy === model.tenantTargetingStrategy)
            return;
        setModel((prevModel) => ({ ...prevModel, tenantTargetingStrategy: strategy, tenantIds: [], rolloutPercentage: undefined }));
    };
    const environmentSummary = () => {
        if (!model.environmentId)
            return Summary.placeholder("Please select an environment");
        return Summary.summary(<EnvironmentOrMissingChip environmentId={model.environmentId} environments={allEnvironments}/>);
    };
    const minimumVersionSummary = () => {
        return model.minimumVersion
            ? Summary.summary(<>
                      Requires minimum release version <code>{model.minimumVersion}</code>
                  </>)
            : Summary.default("No minimum version");
    };
    const excludedTenantSummary = () => {
        const summaryChunks: string[] = [];
        if (model.excludedTenantIds.length > 0) {
            summaryChunks.push(`${pluralize("tenant", model.excludedTenantIds.length, true)}`);
        }
        if (showTenantTagTargetedToggles && model.excludedTenantTags?.length > 0) {
            summaryChunks.push(`${pluralize("tenant tag", model.excludedTenantTags.length, true)}`);
        }
        if (summaryChunks.length === 0) {
            return Summary.default("No excluded tenants");
        }
        const summary = summaryChunks.length === 2 ? summaryChunks.join(" and ") : summaryChunks[0] || "";
        return Summary.summary(summary);
    };
    const includedTenantSummary = () => {
        if (model.tenantTargetingStrategy === "AllTenants") {
            return Summary.default("All tenants");
        }
        const summaryChunks: string[] = [];
        if (model.tenantIds.length > 0) {
            summaryChunks.push(`${pluralize("tenant", model.tenantIds.length, true)}`);
        }
        if (showTenantTagTargetedToggles && model.tenantTags?.length > 0) {
            summaryChunks.push(`${pluralize("tenant tag", model.tenantTags.length, true)}`);
        }
        if (model.rolloutPercentage) {
            summaryChunks.push(`${model.rolloutPercentage}% of all tenants`);
        }
        if (summaryChunks.length === 0) {
            return Summary.summary("Specific tenants");
        }
        const summary = summaryChunks.length === 1 ? summaryChunks[0] : summaryChunks.slice(0, -1).join(", ") + (summaryChunks.length > 2 ? ", and " : " and ") + summaryChunks[summaryChunks.length - 1];
        return Summary.summary(summary);
    };
    const segmentSummary = () => {
        if (!model.segments) {
            return;
        }
        return Summary.summary(<SegmentList segments={model.segments}/>);
    };
    function isEphemeralEnvironment(environmentId: string | undefined) {
        if (!environmentId) {
            return false;
        }
        return allEnvironments.find((e) => e.Id === environmentId)?.Type === "Ephemeral";
    }
    const onCompletion = (model: EditFeatureToggleEnvironmentState) => {
        if (!model.environmentId) {
            return false;
        }
        const isEphemeral = isEphemeralEnvironment(model.environmentId);
        const toggleStrategy: ToggleStrategy = (model.tenantTargetingStrategy === "AllTenants" || isEphemeral) && Object.keys(model.segments).length === 0 ? "EntireEnvironment" : "Targeted";
        let toggleEnvironment = {
            DeploymentEnvironmentId: model.environmentId,
            IsEnabled: featureToggleEnvironment?.IsEnabled ?? true,
            ToggleStrategy: toggleStrategy,
            TenantTargetingStrategy: model.tenantTargetingStrategy,
            TenantIds: model.tenantIds,
            RolloutPercentage: model.rolloutPercentage ?? null,
            ExcludedTenantIds: model.excludedTenantIds,
            TenantTags: model.tenantTags ?? [],
            ExcludedTenantTags: model.excludedTenantTags ?? [],
            Segments: model.segments,
            MinimumVersion: model.minimumVersion ?? null,
        };
        if (isEphemeral) {
            toggleEnvironment = { ...toggleEnvironment, TenantTargetingStrategy: "AllTenants", RolloutPercentage: null, TenantIds: [], ExcludedTenantIds: [], TenantTags: [], ExcludedTenantTags: [] };
        }
        completeWorkflow(toggleEnvironment);
    };
    const { model, setModel, formProps, createSaveAction, getFieldError } = useForm({
        formName: `${title} Drawer`,
        initialModel: initialModel,
        onSubmit: async (_, model) => model,
        afterSubmit: async () => {
            onCompletion(model);
        },
        updateFormModelAfterSubmit: (model) => model,
        submitPermission: {
            permission: Permission.FeatureToggleEdit,
            environment: featureToggleEnvironment?.DeploymentEnvironmentId,
            project: workflowInput.projectId,
        },
        validate: (model) => {
            if (model.rolloutPercentage && (model.rolloutPercentage < 0 || model.rolloutPercentage > 100)) {
                return {
                    errorMessage: "Enter a valid percentage of tenants",
                    fieldErrors: { rolloutPercentage: "Please enter a percentage value from 0 to 100.", includedTenants: "" },
                };
            }
        },
        disableSnackbar: true,
    });
    const showTenantSection = workflowInput.tenantedDeploymentMode !== TenantedDeploymentMode.Untenanted && !isEphemeralEnvironment(model.environmentId);
    return (<WorkflowContent.Drawer workflow={workflow} primaryAction={createSaveAction({ accessibleName: `Confirm ${title}` })} title={title}>
            <Form {...formProps}>
                <ExpandableFormSection title="Environment" summary={environmentSummary()} errorKey="Environment">
                    <ConfigurableEnvironmentSelect allEnvironments={allEnvironments} configurableEnvironments={configurableEnvironments} currentEnvironmentId={model.environmentId} onChange={(selected) => {
            setModel((prevModel) => ({ ...prevModel, environmentId: selected }));
        }} disabled={!adding}/>
                </ExpandableFormSection>
                <ExpandableFormSection title="Minimum Version" summary={minimumVersionSummary()} errorKey="MinimumVersion">
                    <TextField label="Minimum version" value={model.minimumVersion ? model.minimumVersion.toString() : ""} onChange={(version) => setModel((prevModel) => ({ ...prevModel, minimumVersion: !version ? undefined : version }))}/>
                </ExpandableFormSection>
                {showTenantSection && (<>
                        <ExpandableFormSection title="Excluded Tenants" help="Select tenants to exclude for this toggle" summary={excludedTenantSummary()} errorKey={"excludedTenants"}>
                            <div className={styles.formElementContainer}>
                                <MultiSelect label="Tenants to exclude" placeholder="Excluded tenants" items={tenants} value={model.excludedTenantIds} getOption={(tenant) => ({ label: tenant.Name, value: tenant.Id })} onChange={(tenantIds) => setModel((prevModel) => ({ ...prevModel, excludedTenantIds: tenantIds }))} allowClear allowFilter/>
                                {showTenantTagTargetedToggles && (<MultiSelect label="Tenant tags to exclude" placeholder="Excluded tenant tags" items={tenantTags} value={model.excludedTenantTags} getOption={(tenantTag) => ({ label: tenantTag, value: tenantTag })} onChange={(tenantTags) => setModel((prevModel) => ({ ...prevModel, excludedTenantTags: tenantTags }))} allowClear allowFilter/>)}
                            </div>
                        </ExpandableFormSection>
                        <ExpandableFormSection title="Included Tenants" help="Select tenants to include for this toggle" summary={includedTenantSummary()} errorKey={"includedTenants"}>
                            <RadioButtonGroup title="Included tenants" value={model.tenantTargetingStrategy} onChange={(x) => onChangeTenantTargetingStrategy(x)}>
                                <RadioButton label={"All Tenants"} value={"AllTenants"}/>
                                <RadioButton label={"Specific Tenants"} value={"SpecificTenants"}/>
                            </RadioButtonGroup>
                            {model.tenantTargetingStrategy === "SpecificTenants" && (<div className={styles.formElementContainer}>
                                    <NumberField label="Percentage of all tenants" placeholder="0" suffix={"%"} value={model.rolloutPercentage || undefined} 
            // The NumberField component takes either a number or undefined, and undefined is required to clear the field.
            // NumberField changes from controlled to uncontrolled if its value changes from undefined to a number, or vice versa.
            onChange={(percentage) => setModel((prevModel) => ({ ...prevModel, rolloutPercentage: !percentage ? undefined : percentage }))} min={0} max={100} error={getFieldError("rolloutPercentage")}/>
                                    <MultiSelect label="Tenants to include" placeholder="Included tenants" items={tenants} value={model.tenantIds} getOption={(tenant) => ({ label: tenant.Name, value: tenant.Id })} onChange={(tenantIds) => setModel((prevModel) => ({ ...prevModel, tenantIds: tenantIds }))} allowClear allowFilter/>
                                    {showTenantTagTargetedToggles && (<MultiSelect label="Tenant tags to include" placeholder="Included tenant tags" items={tenantTags} value={model.tenantTags} getOption={(tenantTag) => ({ label: tenantTag, value: tenantTag })} onChange={(tenantTags) => setModel((prevModel) => ({ ...prevModel, tenantTags: tenantTags }))} allowClear allowFilter/>)}
                                </div>)}
                        </ExpandableFormSection>
                    </>)}
                <ExpandableFormSection title="Segments" summary={segmentSummary()} errorKey="segments" help="Configure segments to include for this toggle">
                    <SegmentEditList segments={model.segments} setSegments={(items) => setModel((prev) => ({ ...prev, segments: items.map((item) => ({ Key: item.Key, Value: item.Value })) }))}/>
                </ExpandableFormSection>
            </Form>
        </WorkflowContent.Drawer>);
}
